import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Store as StoreIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/api';

interface AdvancedFeatures {
  // Core Business Features
  memberships: boolean;
  loyaltyPoints: boolean;
  homeServices: boolean;
  onlineBooking: boolean;
  walkInBooking: boolean;
  discountEngine: boolean;
  makeupCategories: boolean;
  marketingCampaigns: boolean;
  
  // Brand Protection Features
  premiumServices: boolean;
  luxuryPackages: boolean;
  vipTreatment: boolean;
  brandedProducts: boolean;
  
  // Operational Features
  staffScheduling: boolean;
  inventoryManagement: boolean;
  financialReporting: boolean;
  customerAnalytics: boolean;
  
  // Marketing & Communication
  smsMarketing: boolean;
  emailCampaigns: boolean;
  socialMediaIntegration: boolean;
  reviewManagement: boolean;
  referralPrograms: boolean;
  
  // Advanced Features
  aiRecommendations: boolean;
  predictiveAnalytics: boolean;
  customIntegrations: boolean;
  apiAccess: boolean;
  webhooks: boolean;
}

interface Salon {
  id: string;
  name: string;
  salonType: string;
  positioningLocked: boolean;
  brandProtectionLevel: string;
}

interface AdvancedFeatureControlProps {
  salon: Salon;
  onClose: () => void;
}

const AdvancedFeatureControl: React.FC<AdvancedFeatureControlProps> = ({ salon, onClose }) => {
  const [features, setFeatures] = useState<AdvancedFeatures>({
    // Initialize with default values
    memberships: false,
    loyaltyPoints: false,
    homeServices: false,
    onlineBooking: true,
    walkInBooking: true,
    discountEngine: false,
    makeupCategories: true,
    marketingCampaigns: false,
    premiumServices: false,
    luxuryPackages: false,
    vipTreatment: false,
    brandedProducts: false,
    staffScheduling: true,
    inventoryManagement: true,
    financialReporting: true,
    customerAnalytics: false,
    smsMarketing: false,
    emailCampaigns: false,
    socialMediaIntegration: false,
    reviewManagement: false,
    referralPrograms: false,
    aiRecommendations: false,
    predictiveAnalytics: false,
    customIntegrations: false,
    apiAccess: false,
    webhooks: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    feature: string;
    value: boolean;
  }>({ open: false, feature: '', value: false });

  useEffect(() => {
    fetchFeatures();
  }, [salon.id]);

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/tenants/${salon.id}/config`);
      if (response.success) {
        setFeatures(response.data.config.features || features);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch features');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature: keyof AdvancedFeatures, value: boolean) => {
    // Check if feature change requires confirmation for brand protection
    const restrictedFeatures = ['premiumServices', 'luxuryPackages', 'vipTreatment', 'brandedProducts'];
    
    if (salon.salonType === 'SILENT_UTILITY' && restrictedFeatures.includes(feature) && value) {
      setConfirmDialog({
        open: true,
        feature,
        value
      });
      return;
    }

    updateFeature(feature, value);
  };

  const updateFeature = async (feature: keyof AdvancedFeatures, value: boolean) => {
    try {
      const updatedFeatures = { ...features, [feature]: value };
      setFeatures(updatedFeatures);

      const response = await apiClient.put(`/tenants/${salon.id}/config`, {
        features: updatedFeatures
      });

      if (!response.success) {
        // Revert on failure
        setFeatures(features);
        setError('Failed to update feature');
      }
    } catch (err: any) {
      // Revert on failure
      setFeatures(features);
      setError(err.response?.data?.error?.message || 'Failed to update feature');
    }
  };

  const handleConfirmFeatureChange = () => {
    updateFeature(confirmDialog.feature as keyof AdvancedFeatures, confirmDialog.value);
    setConfirmDialog({ open: false, feature: '', value: false });
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put(`/tenants/${salon.id}/config`, {
        features
      });

      if (response.success) {
        onClose();
      } else {
        setError('Failed to save features');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  const getFeatureGroups = () => [
    {
      title: 'Core Business Features',
      icon: <StoreIcon />,
      features: [
        { key: 'memberships', label: 'Membership Plans', description: 'Enable membership and subscription plans' },
        { key: 'loyaltyPoints', label: 'Loyalty Points', description: 'Customer loyalty points system' },
        { key: 'homeServices', label: 'Home Services', description: 'At-home service bookings' },
        { key: 'onlineBooking', label: 'Online Booking', description: 'Online appointment booking' },
        { key: 'walkInBooking', label: 'Walk-in Booking', description: 'Walk-in customer management' },
        { key: 'discountEngine', label: 'Discount Engine', description: 'Promotional discounts and offers' },
        { key: 'makeupCategories', label: 'Makeup Categories', description: 'Makeup service categories' },
        { key: 'marketingCampaigns', label: 'Marketing Campaigns', description: 'Marketing campaign management' }
      ]
    },
    {
      title: 'Brand Protection Features',
      icon: <SecurityIcon />,
      features: [
        { key: 'premiumServices', label: 'Premium Services', description: 'High-end premium services' },
        { key: 'luxuryPackages', label: 'Luxury Packages', description: 'Luxury service packages' },
        { key: 'vipTreatment', label: 'VIP Treatment', description: 'VIP customer treatment options' },
        { key: 'brandedProducts', label: 'Branded Products', description: 'Branded product sales' }
      ]
    },
    {
      title: 'Operational Features',
      icon: <SettingsIcon />,
      features: [
        { key: 'staffScheduling', label: 'Staff Scheduling', description: 'Advanced staff scheduling' },
        { key: 'inventoryManagement', label: 'Inventory Management', description: 'Product inventory tracking' },
        { key: 'financialReporting', label: 'Financial Reporting', description: 'Detailed financial reports' },
        { key: 'customerAnalytics', label: 'Customer Analytics', description: 'Customer behavior analytics' }
      ]
    },
    {
      title: 'Marketing & Communication',
      icon: <CampaignIcon />,
      features: [
        { key: 'smsMarketing', label: 'SMS Marketing', description: 'SMS marketing campaigns' },
        { key: 'emailCampaigns', label: 'Email Campaigns', description: 'Email marketing campaigns' },
        { key: 'socialMediaIntegration', label: 'Social Media Integration', description: 'Social media platform integration' },
        { key: 'reviewManagement', label: 'Review Management', description: 'Customer review management' },
        { key: 'referralPrograms', label: 'Referral Programs', description: 'Customer referral programs' }
      ]
    },
    {
      title: 'Advanced Features',
      icon: <AnalyticsIcon />,
      features: [
        { key: 'aiRecommendations', label: 'AI Recommendations', description: 'AI-powered service recommendations' },
        { key: 'predictiveAnalytics', label: 'Predictive Analytics', description: 'Predictive business analytics' },
        { key: 'customIntegrations', label: 'Custom Integrations', description: 'Third-party integrations' },
        { key: 'apiAccess', label: 'API Access', description: 'Developer API access' },
        { key: 'webhooks', label: 'Webhooks', description: 'Real-time webhook notifications' }
      ]
    }
  ];

  const getSalonTypeColor = (salonType: string) => {
    switch (salonType) {
      case 'MAIN_BRAND': return 'primary';
      case 'SILENT_UTILITY': return 'warning';
      case 'EXPERIMENTAL': return 'info';
      case 'PARTNER': return 'secondary';
      default: return 'default';
    }
  };

  const getEnabledFeaturesCount = () => {
    return Object.values(features).filter(Boolean).length;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading features...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Advanced Feature Control
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {salon.name}
            </Typography>
            <Chip 
              label={salon.salonType.replace('_', ' ')} 
              color={getSalonTypeColor(salon.salonType) as any}
              size="small"
            />
            {salon.positioningLocked && (
              <Chip 
                label="Positioning Locked" 
                color="error"
                size="small"
                icon={<SecurityIcon />}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" color="primary">
            {getEnabledFeaturesCount()}/25
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Features Enabled
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {salon.salonType === 'SILENT_UTILITY' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is a Silent/Utility salon. Premium features are restricted to maintain brand positioning.
        </Alert>
      )}

      <Grid container spacing={2}>
        {getFeatureGroups().map((group) => (
          <Grid item xs={12} key={group.title}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {group.icon}
                  <Typography variant="h6">{group.title}</Typography>
                  <Chip 
                    label={`${group.features.filter(f => features[f.key as keyof AdvancedFeatures]).length}/${group.features.length}`}
                    size="small"
                    color="primary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {group.features.map((feature) => (
                    <Grid item xs={12} md={6} key={feature.key}>
                      <Card variant="outlined">
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                  {feature.label}
                                </Typography>
                                <Tooltip title={feature.description}>
                                  <IconButton size="small">
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {feature.description}
                              </Typography>
                            </Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={features[feature.key as keyof AdvancedFeatures]}
                                  onChange={(e) => handleFeatureChange(feature.key as keyof AdvancedFeatures, e.target.checked)}
                                  size="small"
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSaveAll}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, feature: '', value: false })}>
        <DialogTitle>Brand Protection Warning</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to enable a premium feature for a Silent/Utility salon.
          </Alert>
          <Typography>
            This action may affect brand positioning. Silent salons are designed to be utility-focused 
            and enabling premium features could create brand confusion.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, feature: '', value: false })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmFeatureChange} color="warning" variant="contained">
            Enable Feature
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedFeatureControl;